import { Check, X } from "lucide-react";
import { fmtUnixDate } from "@/lib/wholesale-format";
import type { WholesaleStatus } from "@/lib/wholesale";
import { cn } from "@/lib/utils";

type Stamps = {
  createdAt: number;
  confirmedAt: number | null;
  packedAt: number | null;
  shippedAt: number | null;
  completedAt: number | null;
  cancelledAt: number | null;
};

type Step = { key: string; label: string; at: number | null; state: "done" | "current" | "skipped" | "todo" | "cancelled" };

/** Status narudžbe koji odgovara svakom koraku vremenske trake — koristi se da se korak nađe
 *  u `order` nizu, umjesto oslanjanja na to da su nizovi pozicijski poravnati. */
const STEP_STATUS: Record<string, WholesaleStatus> = {
  created: "draft",
  confirmed: "confirmed",
  packed: "packed",
  shipped: "shipped",
  completed: "completed",
};

/** Koraci narudžbe redom; „Spakovana" se može preskočiti (potvrđena → poslana), pa ostaje prazna.
 *  Zaprimljena (`submitted`) narudžba nema svoj korak na traci — dijeli korak „Kreirana" s
 *  nacrtom, jer obje čekaju potvrdu operatera (Ruling R24). */
export function buildSteps(status: WholesaleStatus, s: Stamps): Step[] {
  const base: { key: string; label: string; at: number | null }[] = [
    { key: "created", label: "Kreirana", at: s.createdAt },
    { key: "confirmed", label: "Potvrđena", at: s.confirmedAt },
    { key: "packed", label: "Spakovana", at: s.packedAt },
    { key: "shipped", label: "Poslana", at: s.shippedAt },
    { key: "completed", label: "Završena", at: s.completedAt },
  ];
  const order: WholesaleStatus[] = ["draft", "submitted", "confirmed", "packed", "shipped", "completed"];
  const currentIdx = status === "cancelled" ? -1 : order.indexOf(status);

  if (status === "cancelled") {
    // Prikaži sve što je stvarno dosegnuto, pa „Otkazana" kao završni korak.
    const reached = base.filter((b) => b.at !== null);
    return [
      ...reached.map((b): Step => ({ ...b, state: "done" })),
      { key: "cancelled", label: "Otkazana", at: s.cancelledAt, state: "cancelled" },
    ];
  }

  return base.map((b): Step => {
    // `b.key` je uvijek jedan od pet ključeva iz `base` iznad, svi imaju unos u STEP_STATUS.
    const stepIdx = order.indexOf(STEP_STATUS[b.key]!);
    if (stepIdx === currentIdx) return { ...b, state: "current" };
    if (stepIdx < currentIdx) return { ...b, state: b.at === null ? "skipped" : "done" };
    return { ...b, state: "todo" };
  });
}

export function OrderTimeline({ status, stamps, className }: { status: WholesaleStatus; stamps: Stamps; className?: string }) {
  const steps = buildSteps(status, stamps);
  const current = steps.find((s) => s.state === "current" || s.state === "cancelled");
  const next = steps[steps.findIndex((s) => s === current) + 1];
  return (
    <div className={cn("flex flex-col gap-2", className)}>
    <ol className="flex w-full" aria-label="Tok narudžbe">
      {steps.map((st, i) => {
        const last = i === steps.length - 1;
        const filled = st.state === "done" || st.state === "current";
        return (
          <li key={st.key} className="flex-1 min-w-0 flex flex-col gap-1.5">
            <div className="flex items-center">
              <span
                aria-hidden
                className={cn(
                  "shrink-0 size-4 flex items-center justify-center ring-1 transition-colors",
                  st.state === "done" && "bg-foreground ring-foreground text-background",
                  st.state === "current" && "bg-background ring-foreground ring-2",
                  st.state === "skipped" && "bg-background ring-foreground/20",
                  st.state === "todo" && "bg-background ring-foreground/20",
                  st.state === "cancelled" && "bg-destructive ring-destructive text-white",
                )}
              >
                {st.state === "done" && <Check className="size-2.5" strokeWidth={3} />}
                {st.state === "cancelled" && <X className="size-2.5" strokeWidth={3} />}
                {st.state === "current" && <span className="size-1.5 bg-foreground" />}
              </span>
              {!last && (
                <span
                  aria-hidden
                  className={cn("h-px flex-1 mx-1", filled && steps[i + 1]?.state !== "todo" ? "bg-foreground" : "bg-foreground/15")}
                />
              )}
            </div>
            <div className="hidden sm:flex flex-col min-w-0 pr-2">
              <span
                className={cn(
                  "text-[10px] uppercase tracking-[0.08em] truncate",
                  st.state === "current" ? "text-foreground font-medium" : "text-muted-foreground",
                  st.state === "cancelled" && "text-destructive font-medium",
                )}
              >
                {st.label}
                {st.state === "current" && <span className="sr-only"> (trenutno)</span>}
              </span>
              <span className={cn("text-[11px] tabular-nums", st.at ? "text-foreground" : "text-muted-foreground/60")}>
                {st.at ? fmtUnixDate(st.at) : st.state === "skipped" ? "preskočeno" : "—"}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
    {/* Na telefonu nema mjesta za pet natpisa: ispod tačaka ide samo trenutni korak i šta slijedi. */}
    {current && (
      <p className="sm:hidden text-[11px] flex items-baseline gap-1.5 flex-wrap">
        <span className={cn("uppercase tracking-[0.08em] font-medium text-[10px]", current.state === "cancelled" ? "text-destructive" : "text-foreground")}>
          {current.label}
        </span>
        <span className="tabular-nums">{current.at ? fmtUnixDate(current.at) : "—"}</span>
        {next && current.state !== "cancelled" && (
          <span className="text-muted-foreground">· slijedi {next.label.toLowerCase()}</span>
        )}
      </p>
    )}
    </div>
  );
}
